import toNumber from 'lodash/toNumber';
import range from 'lodash/range';
import numeral from 'numeral';
import { numberFix, numberWithCommas, numberWithCommasAndDecimal } from './helper';

export const currencyValueFormatterWithNoValue = params => {
  const value = +params.value;

  if (!value) {
    return '-';
  }

  return value < 0.01 ? '< $0.01' : currencyFormat(value);
};

export const numberValueFormatter = params => numberWithCommas(+params.value);
export const currencyValueFormatter = params => currencyFormat(+params.value);
export const decimalValueFormatter = params => numberWithCommasAndDecimal(+params.value);
export const percentageValueFormatter = params => percentageFormat(+params.value);

function getFormatTemplate({ currency, precision }) {
  if (currency) {
    return `$0,0.${range(precision).join('')}`;
  }

  return `0,0.${range(precision).join('')}`;
}

export function getPercentage(newValue, oldValue, precision = 2) {
  let n = newValue,
    o = oldValue;

  if (n > 0 && n < 1) {
    n = 1;
  }

  if (o > 0 && o < 1) {
    o = 1;
  }

  const newNumber = toNumber(n);
  const oldNumber = toNumber(o);
  const increase = newNumber - oldNumber;

  if (!increase || !oldNumber) {
    return 0;
  }

  return numberFix((increase / Math.abs(oldNumber)) * 100, precision);
}

export function currencyFormat(value = 0) {
  if (value === '0.00' || value === 0) {
    return '$0.00';
  }

  const isNumber =
    numeral(value).format(0, 0.0) !== 'NaN' &&
    numeral(value).format('$0,0.00') !== 'N$aN' &&
    numeral(value).format('$0,0.00') !== '0$.00';

  return isNumber ? numeral(value).format('$0,0.00') : '$0.00';
}

export function percentageFormat(value = 0) {
  if (value === null || value === undefined || isNaN(value) || value === '') {
    return '-';
  }

  if (value === 0) {
    return '0.00%';
  }

  const absValue = Math.abs(value);
  const formattedValue = absValue.toFixed(2);
  const sign = value > 0 ? '+' : '-';

  return `${sign}${formattedValue}%`;
}

export function countDecimals(value) {
  if (Math.floor(value) === value) {
    return 0;
  }

  return value.toString().split('.')[1]?.length || 0;
}

export function convertLetterToNumber(str) {
  let out = 0;
  const len = str.length;

  for (let pos = 0; pos < len; pos++) {
    out += (str.charCodeAt(pos) - 64) * Math.pow(26, len - pos - 1);
  }

  return out;
}

export function currencyFormatterWithNoValue(value = 0) {
  const tempValue = +value;

  if (!tempValue) {
    return '-';
  }

  return tempValue < 0.01 ? '< $0.01' : currencyFormat(tempValue);
}

export function numberFormatter(value = 0, format = {}) {
  const { currency = false, percentage = false, precision = 2 } = format;
  let num;

  if (typeof value === 'string') {
    if (value.trim() === '') {
      num = 0;
    } else {
      num = parseFloat(value);
    }
  } else {
    num = Number(value);
  }

  if (isNaN(num) || !Number.isFinite(num)) {
    if (percentage) {
      return '0%';
    }

    if (currency) {
      return '$0.00';
    }

    return '0';
  }

  if (Math.abs(num) < 1e-12) {
    if (percentage) {
      return '0%';
    }

    if (currency) {
      return '$0.00';
    }

    return '0';
  }

  const testFormat = numeral(num).format('0,0.00');

  if (testFormat === 'NaN' || testFormat === 'N$aN') {
    const fixedValue = num.toFixed(precision);
    const formattedValue = currency ? `$${fixedValue}` : fixedValue;

    return percentage ? formattedValue + '%' : formattedValue;
  }

  const decimals = num ? (num.toString().split('.')[1] || '').length : 0;
  const applyPrecision = decimals > precision;

  const formatTmpl = getFormatTemplate({
    currency,
    precision: applyPrecision ? precision : decimals,
  });
  const formattedValue = numeral(num).format(formatTmpl);

  return percentage ? formattedValue + '%' : formattedValue;
}

export const formatRAM = ram => {
  if (!ram || ram < 0) {
    return ['0', 'B'];
  }

  // Use simple number formatting without commas for memory values
  const formatMemoryValue = value => {
    const num = Number(value);

    if (num === 0) {
      return '0';
    }

    if (num % 1 === 0) {
      return num.toString();
    }

    return num.toFixed(2);
  };

  if (ram < 1024) {
    return [formatMemoryValue(ram), 'B'];
  } else if (ram < 1024 ** 2) {
    return [formatMemoryValue(ram / 1024), 'KiB'];
  } else if (ram < 1024 ** 3) {
    return [formatMemoryValue(ram / 1024 ** 2), 'MiB'];
  } else if (ram < 1024 ** 4) {
    return [formatMemoryValue(ram / 1024 ** 3), 'GiB'];
  } else {
    return [formatMemoryValue(ram / 1024 ** 4), 'TiB'];
  }
};
